import { CollapseCard } from '@components/card/collapse-card';
import { createForm, useCreateFormStore } from '@components/form';
import { IconInput } from '@components/icons/input';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUpdateIntegrationEndpoint } from 'api/integrations/hooks';
import { IntegrationEndpoint } from 'api/integrations/requests';

import { FieldTable, getFormEndpointVariables, getVariableCount } from './common';

type Data = {};

const Form = createForm<Data>();

export const ResponseValueCard = ({ endpoint }: { endpoint: IntegrationEndpoint<string> }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const responseVariables = endpoint.responseValues as IntegrationEndpoint['responseValues'];

  const updateIntegrationEndpointMutation = useUpdateIntegrationEndpoint();
  const visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    format: true,
  };

  const handleUpdateEndpoint = async () => {
    const endpointResponseValues = getFormEndpointVariables({
      formStore,
      endpointVariables: responseVariables,
      visibleColumns,
    });
    await updateIntegrationEndpointMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: {
        responseValues: endpointResponseValues,
      },
    });
  };

  const handleRemoveField = async (name: string) => {
    const newVariables = responseVariables
      .map((variable) => {
        if (variable.type === 'array' && name.startsWith(`${variable.name}__`)) {
          const nestedFieldName = name.split(`${variable.name}__`)[1];
          const newTemplate = variable.template?.filter(
            (templateField) => templateField.name !== nestedFieldName
          );

          return { ...variable, template: newTemplate };
        }
        return variable;
      })
      .filter((variable) => {
        return (
          variable.name !== name && !(variable.type === 'array' && variable.template?.length === 0)
        );
      });
    await updateIntegrationEndpointMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: {
        responseValues: newVariables,
      },
    });
  };

  const formActionPortalId = `${endpoint.shortId}-response-values-form-actions-portal`;

  return (
    <CollapseCard
      key={endpoint.shortId}
      keepCollapsedInDom
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <Form store={formStore} onSubmit={handleUpdateEndpoint}>
          {!responseVariables || Object.keys(responseVariables).length === 0 ? (
            <Stack>
              <Text font="regular14" color="textSecondary" text={t('noResponseValues')} />
              <Text font="regular14" color="textSecondary" text={t('afterSuccessfulResponse')} />
            </Stack>
          ) : (
            <FieldTable
              endpointId={endpoint.shortId}
              form={Form}
              endpointVariables={responseVariables}
              visibleColumns={visibleColumns}
              handleRemoveField={handleRemoveField}
              showArrayType
            />
          )}
          {isDirty && (
            <Form.Actions portal={formActionPortalId}>
              <Stack orientation="horizontal" space={2} align="center">
                <Form.Reset stopPropagation text={t('core.cancel')} variant="secondary" />
                <Form.Submit
                  stopPropagation
                  isDisabled={updateIntegrationEndpointMutation.isLoading}
                  text={t('core.save')}
                  variant="primary-blue"
                />
              </Stack>
            </Form.Actions>
          )}
        </Form>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" align="center" space={2}>
          <IconInput color="elementPurple50" />
          <Text text={t('responseValues')} font="semibold14" />
          <Text text="•" color="textHelper" />
          <Text text={getVariableCount(responseVariables)} font="semibold14" />
        </Stack>
        <Stack orientation="horizontal" space={4} align="center">
          <div id={formActionPortalId} />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const locale = {
  en: {
    responseValues: 'Response values',
    noResponseValues:
      'No values received, please add your test request values in the API call and click test',
    afterSuccessfulResponse:
      'Once you receive a successful response the mappable values will appear here',
  },
};
